/* Signals strip + ambient burn meter (spec §5.5) — the backbone's
 * provenance-must-render surface. Every stamped state has copy here:
 * sensors not wired, sensor ran + quiet ("No signals observed" is EARNED —
 * shown only because the sweep really runs), budget-held, per-signal rows.
 * Numbers here are DERIVED server-side (burn = SUM of ledgered wake costs);
 * this component never computes or invents one. */
import { html, type TemplateResult } from 'lit-html';
import {
  getSignals,
  getWakes,
  type Burn,
  type Signal,
  type SensorAvailability,
  type Wake,
} from '../../api/experts.ts';

interface StripData {
  signals: Signal[];
  sensors: SensorAvailability;
  lastSweptAt: string | null;
  wakes: Wake[];
  burn: Burn | null;
}

type StripState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; data: StripData };

interface CacheEntry {
  state: StripState;
  fetchedAt: number;
  inFlight: boolean;
}

const READY_TTL_MS = 60_000;
const cache = new Map<string, CacheEntry>();

function isStale(entry: CacheEntry, now: number): boolean {
  if (entry.state.phase === 'ready') return now - entry.fetchedAt > READY_TTL_MS;
  if (entry.state.phase === 'error') return true;
  return false;
}

function load(slug: string, rerender: () => void): void {
  const current = cache.get(slug);
  if (current?.inFlight) return;
  cache.set(slug, current
    ? { ...current, inFlight: true }
    : { state: { phase: 'loading' }, fetchedAt: Date.now(), inFlight: true });
  Promise.all([getSignals(slug), getWakes(slug)])
    .then(([sig, wk]) => {
      cache.set(slug, {
        state: {
          phase: 'ready',
          data: {
            signals: sig.signals,
            sensors: sig.sensors,
            lastSweptAt: sig.lastSweptAt,
            wakes: wk.wakes,
            burn: wk.burn,
          },
        },
        fetchedAt: Date.now(),
        inFlight: false,
      });
      rerender();
    })
    .catch((e: unknown) => {
      const failedEntry: CacheEntry = {
        state: { phase: 'error', message: String(e) },
        fetchedAt: Date.now(),
        inFlight: true,
      };
      cache.set(slug, failedEntry);
      rerender();
      if (cache.get(slug) === failedEntry) cache.set(slug, { ...failedEntry, inFlight: false });
    });
}

function usd(n: number): string {
  return `$${n.toFixed(2)}`;
}

function evidenceHref(source: string): string | null {
  try {
    const url = new URL(source);
    return url.protocol === 'http:' || url.protocol === 'https:' ? source : null;
  } catch {
    return null;
  }
}

/** Pure render body — exported for unit tests. */
export function renderSignalsBody(data: StripData): TemplateResult {
  const { signals, sensors, lastSweptAt, burn } = data;
  const visible = signals.filter((s) => s.state !== 'dismissed');
  const active = Object.entries(sensors).filter(([, v]) => v === 'active').map(([k]) => k);
  const notWired = Object.entries(sensors).filter(([, v]) => v === 'not-wired').map(([k]) => k);
  const otherStates = Object.entries(sensors)
    .filter(([, v]) => v !== 'active' && v !== 'not-wired')
    .map(([aspect, state]) => `${aspect} ${state}`);
  const held = visible.some((s) => s.state === 'budget-held')
    || (burn !== null && burn.monthUsd >= burn.budgetUsd);
  const wakeCopy = `${burn?.monthWakes ?? 0} wakes this month`;
  const burnCopy = burn === null
    ? `budget meter unavailable — server did not report burn · ${wakeCopy}`
    : `${usd(burn.monthUsd)} of ${usd(burn.budgetUsd)} ambient budget used this month · ${wakeCopy}`;
  const noSignalsCopy = lastSweptAt
    ? `No signals observed — sensors (${active.join(', ') || 'none'}) are watching; a quiet client is a real result, not a blank.`
    : 'No completed sensor sweep for this client yet — quiet is not yet a claim.';
  const sensorStates = [
    active.length ? `${active.join(', ')} active` : 'none active',
    ...otherStates,
    ...(notWired.length
      ? [`${notWired.join(', ')} not wired — those experts run on request only.`]
      : []),
  ];
  const sensorsFooter = `Sensors: ${sensorStates.join(' · ')}`;
  return html`<section class="hrail-card sig-strip">
    <span class="hrail-eyebrow">Intelligence signals</span>
    <p class="sig-burn">${burnCopy}</p>
    ${held
      ? html`<p class="sig-held">ambient budget reached — sensors still watching; experts resume next month or on your request</p>`
      : null}
    ${visible.length === 0
      ? html`<p class="hub-muted">${noSignalsCopy}</p>`
      : html`<ul class="sig-list">
          ${visible.map(
            (s) => {
              const source = s.evidence && (
                typeof s.evidence.url === 'string'
                  ? s.evidence.url
                  : typeof s.evidence.diffUrl === 'string'
                    ? s.evidence.diffUrl
                    : null
              );
              const href = source ? evidenceHref(source) : null;
              return html`<li class="sig-row sig-${s.severity}">
              <span class="sig-kind">${s.kind}</span>
              <span class="sig-meta">${s.aspect} · ${s.severity} · ${s.observed_at.slice(0, 10)}</span>
              <span class="sig-state">${s.state === 'budget-held' ? 'held — budget' : s.state}</span>
              ${s.evidenceCorrupt
                ? html`<span class="sig-evidence">evidence unreadable</span>`
                : href
                  ? html`<a class="sig-source" href=${href} target="_blank" rel="noopener">source ↗</a>`
                  : source
                    ? html`<span class="sig-source">source</span>`
                  : null}
            </li>`;
            },
          )}
        </ul>`}
    <p class="hub-muted sig-foot">${sensorsFooter}</p>
  </section>`;
}

export function renderSignalsStrip(slug: string, rerender: () => void): TemplateResult {
  const entry = cache.get(slug);
  if (!entry) {
    load(slug, rerender);
    return html`<section class="hrail-card sig-strip">
      <span class="hrail-eyebrow">Intelligence signals</span>
      <p class="hub-muted">Loading signals…</p>
    </section>`;
  }
  if (isStale(entry, Date.now()) && !entry.inFlight) load(slug, rerender);
  const { state } = entry;
  if (state.phase === 'loading') {
    return html`<section class="hrail-card sig-strip">
      <span class="hrail-eyebrow">Intelligence signals</span>
      <p class="hub-muted">Loading signals…</p>
    </section>`;
  }
  if (state.phase === 'error') {
    const errorCopy = `Signals unavailable — ${state.message}. Nothing is fabricated here.`;
    return html`<section class="hrail-card sig-strip">
      <span class="hrail-eyebrow">Intelligence signals</span>
      <p class="hub-muted">${errorCopy}</p>
    </section>`;
  }
  return renderSignalsBody(state.data);
}
